function pauseOtherStoryMedia(current) {
  document.querySelectorAll('story-media-sound, story-video').forEach((player) => {
    if (player === current) return;
    const video = player.video || player.querySelector('video');
    if (!video) return;
    const isPlaying = !video.paused;
    const isAudible = !video.muted && video.volume > 0;
    if (!isPlaying && !isAudible) return;
    if (isPlaying) video.pause();
    video.muted = true;
    if (player.video && typeof player.syncPlayback === 'function') player.syncPlayback();
    if (player.video && typeof player.syncMute === 'function') player.syncMute();
  });
}

if (!customElements.get('story-media-sound')) {
  customElements.define(
    'story-media-sound',
    class StoryMediaSound extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.initialized = true;

        this.playButton = this.querySelector('[data-story-play]');
        this.muteButton = this.querySelector('[data-story-mute]');

        this.playButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.togglePlay();
        });

        this.video.addEventListener('click', () => this.togglePlay());

        this.muteButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const turnSoundOn = this.video.muted || this.video.volume === 0;
          if (turnSoundOn) {
            pauseOtherStoryMedia(this);
            this.video.muted = false;
            this.video.volume = 1;
            if (this.video.paused) {
              const playback = this.video.play();
              if (playback) {
                playback.catch(() => {
                  this.video.muted = true;
                  this.syncMute();
                });
              }
            }
          } else {
            this.video.muted = true;
          }
          this.syncMute();
        });

        this.video.addEventListener('play', () => pauseOtherStoryMedia(this));
        ['play', 'pause', 'ended'].forEach((name) => this.video.addEventListener(name, () => this.syncPlayback()));
        this.video.addEventListener('volumechange', () => this.syncMute());
        this.syncPlayback();
        this.syncMute();
      }

      togglePlay() {
        if (this.video.paused) {
          pauseOtherStoryMedia(this);
          this.video.muted = false;
          this.video.volume = 1;
          const playback = this.video.play();
          if (playback) {
            playback.catch(() => {
              this.video.muted = true;
              this.video.play().catch(() => {});
              this.syncMute();
            });
          }
        } else {
          this.video.pause();
        }
      }

      syncPlayback() {
        const isPlaying = !this.video.paused;
        this.classList.toggle('is-playing', isPlaying);
        this.playButton?.setAttribute('aria-label', isPlaying ? this.dataset.labelPause : this.dataset.labelPlay);
      }

      syncMute() {
        const isMuted = this.video.muted || this.video.volume === 0;
        this.classList.toggle('is-muted', isMuted);
        this.muteButton?.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
        this.muteButton?.setAttribute('aria-label', isMuted ? this.dataset.labelUnmute : this.dataset.labelMute);
      }
    }
  );
}

if (!customElements.get('story-video')) {
  customElements.define(
    'story-video',
    class StoryVideo extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.initialized = true;

        this.playButton = this.querySelector('[data-story-play]');
        this.muteButton = this.querySelector('[data-story-mute]');
        this.timeDisplay = this.querySelector('[data-story-time]');

        this.playButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.togglePlay();
        });

        this.video.addEventListener('click', () => this.togglePlay());
        this.video.addEventListener('play', () => pauseOtherStoryMedia(this));

        this.muteButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.video.muted) pauseOtherStoryMedia(this);
          this.video.muted = !this.video.muted;
          if (!this.video.muted && this.video.paused) this.video.play();
        });

        ['play', 'pause', 'ended'].forEach((name) => this.video.addEventListener(name, () => this.syncPlayback()));
        this.video.addEventListener('volumechange', () => this.syncMute());
        ['timeupdate', 'loadedmetadata'].forEach((name) => this.video.addEventListener(name, () => this.updateTime()));

        this.syncPlayback();
        this.syncMute();
        if (this.video.readyState >= 1) this.updateTime();
      }

      togglePlay() {
        if (this.video.paused) {
          pauseOtherStoryMedia(this);
          this.video.muted = false;
          this.video.play();
        } else {
          this.video.pause();
        }
      }

      syncPlayback() {
        this.classList.toggle('is-playing', !this.video.paused);
        this.playButton?.setAttribute('aria-label', this.video.paused ? this.dataset.labelPlay : this.dataset.labelPause);
      }

      syncMute() {
        const isMuted = this.video.muted || this.video.volume === 0;
        this.classList.toggle('is-muted', isMuted);
        this.muteButton?.setAttribute('aria-label', isMuted ? this.dataset.labelUnmute : this.dataset.labelMute);
      }

      updateTime() {
        if (!this.timeDisplay) return;
        this.timeDisplay.textContent = `${this.formatTime(this.video.currentTime)} / ${this.formatTime(this.video.duration)}`;
      }

      formatTime(seconds) {
        if (!Number.isFinite(seconds)) return '0:00';
        const minutes = Math.floor(seconds / 60);
        const remainder = Math.floor(seconds % 60);
        return `${minutes}:${remainder < 10 ? '0' : ''}${remainder}`;
      }
    }
  );
}

if (!customElements.get('story-countdown')) {
  customElements.define(
    'story-countdown',
    class StoryCountdown extends HTMLElement {
      connectedCallback() {
        this.valueElement = this.querySelector('[data-countdown-value]');
        if (!this.valueElement) return;
        this.endTime = this.getEndTime();
        this.tick();
      }

      disconnectedCallback() {
        clearTimeout(this.timeout);
      }

      getEndTime() {
        if (this.dataset.mode !== 'fixed_duration') {
          return new Date(this.dataset.endDate).getTime();
        }

        const storageKey = `story-countdown-${this.dataset.key}`;
        const durationMs = (parseFloat(this.dataset.hours) || 24) * 3600000;
        let stored = null;
        try {
          stored = parseInt(localStorage.getItem(storageKey), 10);
        } catch (error) {}

        const endTime = stored || Date.now() + durationMs;
        try {
          localStorage.setItem(storageKey, endTime);
        } catch (error) {}
        return endTime;
      }

      tick() {
        const remaining = this.endTime - Date.now();
        if (!Number.isFinite(remaining) || remaining <= 0) {
          this.valueElement.textContent = '00:00:00';
          return;
        }

        const pad = (number) => String(number).padStart(2, '0');
        this.valueElement.textContent = [
          Math.floor(remaining / 3600000),
          Math.floor((remaining % 3600000) / 60000),
          Math.floor((remaining % 60000) / 1000),
        ]
          .map(pad)
          .join(':');

        this.timeout = setTimeout(() => this.tick(), 1000);
      }
    }
  );
}
